import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet, { BottomSheetScrollView, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { Svg, Path } from 'react-native-svg';
import Avatar from './Avatar';
import VerifiedBadgeIcon from './icons/VerifiedBadgeIcon';
import BottomSheetHeader from './BottomSheetHeader';
import { Sponsor } from '../services/sponsorService';
import ProfessionalDetails, { buildProfessionalDetailRows } from './ProfessionalDetails';
import { useLanguage } from '@/contexts/LanguageContext';

interface SponsorProfileSheetProps {
  sponsor: Sponsor | null;
  isVisible: boolean;
  onClose: () => void;
  onSchedule: () => void;
}

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function formatVerifiedDate(dateStr?: string): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${MONTHS_PT[d.getMonth()]} de ${d.getFullYear()}`;
}

const CalendarIcon = () => (
  <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <Path d="M15.1523 3.69238V6.6677" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M7.92969 3.69238V6.6677" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path fillRule="evenodd" clipRule="evenodd" d="M15.683 5.06445L7.40863 5.06527C4.97812 5.06527 3.46094 6.41874 3.46094 8.90651V16.3957C3.46094 18.9229 4.97812 20.3077 7.40863 20.3077L15.6759 20.3068C18.1136 20.3068 19.6235 18.9462 19.6235 16.4576V8.90569C19.6307 6.41792 18.1207 5.06445 15.683 5.06445Z" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M7.52344 11.4375H7.53344" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M7.52344 14.8916H7.53344" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M11.5391 11.4375H11.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M11.5391 14.8916H11.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M15.5391 11.4375H15.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M15.5391 14.8916H15.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function SponsorProfileSheet({
  sponsor,
  isVisible,
  onClose,
  onSchedule,
}: SponsorProfileSheetProps) {
  const { t, language } = useLanguage();
  const insets = useSafeAreaInsets();
  const screenHeight = Dimensions.get('window').height;
  const snapPoints = useMemo(() => ['92%'], []);
  const professionalDetailLabels = useMemo(
    () => ({
      crp: t('sponsors.details.crp'),
      session: t('sponsors.details.session'),
      price: t('sponsors.details.price'),
    }),
    [t]
  );
  const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
  const formatSessionDuration = useCallback(
    (minutes: number) =>
      t('sponsors.details.durationMinutes', { minutes: String(minutes) }),
    [t]
  );
  const professionalDetailRows = useMemo(
    () =>
      buildProfessionalDetailRows(
        sponsor || { crp: null, telemed: null },
        professionalDetailLabels,
        locale,
        formatSessionDuration
      ),
    [formatSessionDuration, locale, professionalDetailLabels, sponsor]
  );

  const renderBackdrop = useCallback(
    (props: any) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        opacity={0.4}
        pressBehavior="close"
        style={[props.style, { height: screenHeight }]}
      />
    ),
    [screenHeight]
  );

  const handleSheetChange = useCallback(
    (index: number) => {
      if (index === -1) onClose();
    },
    [onClose]
  );

  if (!isVisible || !sponsor) return null;

  const verifiedDate = formatVerifiedDate(sponsor.created_at);

  return (
    <BottomSheet
      index={0}
      snapPoints={snapPoints}
      onChange={handleSheetChange}
      enablePanDownToClose
      enableDynamicSizing={false}
      backgroundStyle={styles.sheetBackground}
      handleIndicatorStyle={styles.handleIndicator}
      backdropComponent={renderBackdrop}
      animateOnMount
    >
      <BottomSheetHeader title="Profissional" onClose={onClose} />

      <BottomSheetScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero — avatar, name, role */}
        <View style={styles.heroSection}>
          <Avatar
            uri={sponsor.photo_url}
            size={88}
            style={{ borderRadius: 44 }}
            showInitials
            initials={sponsor.name}
            loading={false}
          />
          <View style={styles.nameGroup}>
            <View style={styles.nameRow}>
              <Text style={styles.name}>{sponsor.name}</Text>
              <VerifiedBadgeIcon size={20} />
            </View>
            <Text style={styles.role}>{sponsor.role}</Text>
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* About me */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Sobre mim</Text>
          {verifiedDate ? (
            <View style={styles.verifiedRow}>
              <VerifiedBadgeIcon size={18} />
              <Text style={styles.verifiedText}>Verificado em {verifiedDate}</Text>
            </View>
          ) : (
            <View style={styles.verifiedRow}>
              <VerifiedBadgeIcon size={18} />
              <Text style={styles.verifiedText}>Profissional verificado</Text>
            </View>
          )}
          <Text style={styles.bio}>{sponsor.bio}</Text>
        </View>

        {/* Professional details */}
        {professionalDetailRows.length > 0 && (
          <>
            <View style={styles.divider} />
            <View style={styles.detailsSection}>
              <ProfessionalDetails
                sponsor={sponsor}
                labels={professionalDetailLabels}
                locale={locale}
                formatSessionDuration={formatSessionDuration}
              />
            </View>
          </>
        )}

        {/* Divider */}
        {sponsor.interests.length > 0 && <View style={styles.divider} />}

        {/* Interests */}
        {sponsor.interests.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>Assuntos principais</Text>
            <View style={styles.pillsContainer}>
              {sponsor.interests.map((interest, idx) => (
                <View key={idx} style={styles.pill}>
                  <Text style={styles.pillText}>{interest}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
      </BottomSheetScrollView>

      {/* Fixed CTA */}
      <View style={[styles.buttonSection, { paddingBottom: Math.max(insets.bottom, 16) }]}>
        <TouchableOpacity style={styles.scheduleButton} onPress={onSchedule} activeOpacity={0.8}>
          <CalendarIcon />
          <Text style={styles.scheduleButtonText}>{t('sponsors.scheduleSession')}</Text>
        </TouchableOpacity>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  sheetBackground: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 24,
  },
  handleIndicator: {
    backgroundColor: '#DAD8CF',
    width: 40,
    height: 4,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  heroSection: {
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 24,
    paddingHorizontal: 24,
    gap: 16,
  },
  nameGroup: {
    alignItems: 'center',
    gap: 6,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  name: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    color: '#151515',
  },
  role: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#6B9EC2',
    textAlign: 'center',
  },
  divider: {
    height: 1,
    backgroundColor: '#F9F8F3',
    marginHorizontal: 0,
  },
  section: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 12,
  },
  detailsSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  sectionLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 14,
    color: '#7E7D75',
  },
  verifiedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  verifiedText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#151515',
  },
  bio: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#151515',
  },
  pillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pill: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
  },
  pillText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16.8,
    color: '#151515',
  },
  buttonSection: {
    paddingHorizontal: 24,
    paddingTop: 12,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
  },
  scheduleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#151515',
    borderRadius: 12,
    height: 48,
    gap: 8,
  },
  scheduleButtonText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#FFFFFF',
  },
});
